import ThemeResolver from 'resolvers/RTMResolvers/ThemeResolver';

export default (() => {
  /**
   * @type {ThemeResolver}
   */
  let themeResolver;
  let isRegistered = false;

  /**
   * Fetch a list of themes
   * @returns {Promise.<[ThemeDto]>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeList = async () => {
    const themes = await themeResolver.authorThemeList();
    return themes;
  };

  /**
   * Fetch a theme
   * @param {string} themeId
   * @returns {Promise.<[ThemeDto]>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeGet = async ({
    themeId,
  }) => {
    const themes = await themeResolver.authorThemeGet({
      themeId,
    });
    return themes;
  };

  /**
   * Create a theme
   * @param {string} workspaceId
   * @param {string} name
   * @param {string} config
   * @returns {Promise.<[ThemeDto]>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeCreate = async ({
    workspaceId,
    name,
    config,
  }) => {
    const themes = await themeResolver.authorThemeCreate({
      workspaceId,
      name,
      config,
    });
    return themes;
  };

  /**
   * Update a theme
   * @param {string} themeId
   * @param {string} name
   * @param {string} config
   * @returns {Promise.<ThemeDto>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeUpdate = async ({
    themeId,
    name,
    config,
  }) => {
    const themes = await themeResolver.authorThemeUpdate({
      themeId,
      name,
      config,
    });
    return themes;
  };

  /**
   * Delete a theme
   * @param {string} themeId
   * @returns {Promise.<ThemeDto>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeDelete = async ({
    themeId,
  }) => {
    const response = await themeResolver.authorThemeDelete({
      themeId,
    });
    return response;
  };

  /**
   * Gets a theme variant
   * @param {string} themeId
   * @param {string} variantName
   * @returns {Promise.<ThemeDto>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeVariantGet = async ({
    themeId,
    variantId,
  }) => {
    const response = await themeResolver.authorThemeVariantGet({
      themeId,
      variantId,
    });
    return response;
  };

  /**
   * Create a theme variant
   * @param {string} themeId
   * @param {string} variantName
   * @param {string} config
   * @param {string} state
   * @returns {Promise.<ThemeDto>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeVariantCreate = async ({
    themeId,
    variantName,
    config,
    state,
  }) => {
    const response = await themeResolver.authorThemeVariantCreate({
      themeId,
      variantName,
      config,
      state,
    });
    return response;
  };

  /**
   * Update a theme variant
   * @param {string} themeId
   * @param {string} variantName
   * @returns {Promise.<ThemeDto>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeVariantUpdate = async ({
    themeId,
    variantId,
    variantName,
    config,
  }) => {
    const response = await themeResolver.authorThemeVariantUpdate({
      themeId,
      variantId,
      variantName,
      config,
    });
    return response;
  };

  /**
   * Delete a theme variant
   * @param {string} themeId
   * @param {string} variantName
   * @returns {Promise.<ThemeDto>} [ThemeDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/theme-dto/)
   */
  const authorThemeVariantDelete = async ({
    themeId,
    variantId,
  }) => {
    const response = await themeResolver.authorThemeVariantDelete({
      themeId,
      variantId,
    });
    return response;
  };

  /**
   * Fetches a summary list of collaborators belonging to given theme
   */
  const authorThemeCollaboratorSummary = async (themeId) => {
    const collaborators = await themeResolver.authorThemeCollaboratorSummary({ themeId });
    return collaborators;
  };

  /**
   * Grant permission to an account over a theme
   */
  const themePermissionGrant = async (accountOrTeamIds, collaboratorType, themeId, permissionLevel) => {
    const response = await themeResolver.themePermissionGrant({
      accountOrTeamIds,
      themeId,
      collaboratorType,
      permissionLevel,
    });
    return response;
  };
  
  /**
     * Revoke permission of an account over a theme
     */
  const themePermissionRevoke = async (accountOrTeamId, collaboratorType, themeId) => {
    const response = await themeResolver.themePermissionRevoke({
      accountOrTeamId,
      collaboratorType,
      themeId,
    });
    return response;
  };

  return {
    isRegistered: () => isRegistered,

    register: (connectionProvider) => {
      isRegistered = true;
      themeResolver = new ThemeResolver(connectionProvider);
    },

    authorThemeList,
    authorThemeGet,
    authorThemeCreate,
    authorThemeUpdate,
    authorThemeDelete,

    authorThemeVariantGet,
    authorThemeVariantCreate,
    authorThemeVariantUpdate,
    authorThemeVariantDelete,
    
    authorThemeCollaboratorSummary,
    themePermissionGrant,
    themePermissionRevoke,
  };
})();
